import { CallBack, ICommonCallbackPayload } from 'types/common';
import { LogoutPayload, TokenType, UserPayload } from 'types/user';

import { createPayloadAction } from '../helpers';
import { StandardAction } from '../types';
import { IAuthPayload } from './types';

export default class AuthAction {
  static TYPES = {
    // Async actions
    // -- Sign in
    SIGNIN: {
      START: 'auth/SIGNIN_START',
      SUCCESS: 'auth/SIGNIN_SUCCESS',
      FAILURE: 'auth/SIGNIN_FAILURE',
    },

    // -- Sign out
    SIGNOUT: {
      START: 'auth/SIGNOUT_START',
      SUCCESS: 'auth/SIGNOUT_SUCCESS',
      FAILURE: 'auth/SIGNOUT_FAILURE',
    },

    // -- Sign up
    SIGNUP: {
      START: 'auth/SIGNUP_START',
      SUCCESS: 'auth/SIGNUP_SUCCESS',
      FAILURE: 'auth/SIGNUP_FAILURE',
    },

    // -- Reset Password
    RESET_PASSWORD: {
      START: 'auth/RESET_PASSWORD_START',
      SUCCESS: 'auth/RESET_PASSWORD_SUCCESS',
      FAILURE: 'auth/RESET_PASSWORD_FAILURE',
    },

    VERIFY_TOKEN: {
      START: 'auth/VERIFY_TOKEN_START',
      SUCCESS: 'auth/VERIFY_TOKEN_SUCCESS',
      FAILURE: 'auth/VERIFY_TOKEN_FAILURE',
    },

    GET_NEW_ACCESS_TOKEN: {
      START: 'auth/GET_NEW_ACCESS_TOKEN_START',
      END: 'auth/GET_NEW_ACCESS_TOKEN_END',
    },

    // Sync actions
    INIT_URL: 'auth/INIT_URL',
  };

  static userSignUp = (payload: UserPayload): StandardAction<UserPayload> =>
    createPayloadAction(AuthAction.TYPES.SIGNUP.START, payload);

  static userSignUpSuccess = (accessToken: string): StandardAction<string> =>
    createPayloadAction(AuthAction.TYPES.SIGNUP.SUCCESS, accessToken);

  static userSignUpError = (error?: string): StandardAction =>
    createPayloadAction(AuthAction.TYPES.SIGNUP.FAILURE, undefined, undefined, error);

  static userSignIn = (payload: UserPayload): StandardAction<UserPayload> =>
    createPayloadAction(AuthAction.TYPES.SIGNIN.START, payload);

  static userSignInSuccess = (accessToken: string, expires: number): StandardAction<IAuthPayload> =>
    createPayloadAction(AuthAction.TYPES.SIGNIN.SUCCESS, { accessToken, expires });

  static userSignInFailure = (error?: string): StandardAction =>
    createPayloadAction(AuthAction.TYPES.SIGNIN.FAILURE, undefined, undefined, error);

  static userSignOut = (payload?: LogoutPayload): StandardAction<LogoutPayload> =>
    createPayloadAction(AuthAction.TYPES.SIGNOUT.START, payload);

  static userSignOutSuccess = (accessToken?: string): StandardAction<string> =>
    createPayloadAction(AuthAction.TYPES.SIGNOUT.SUCCESS, accessToken);

  static userSignOutError = (error?: string): StandardAction =>
    createPayloadAction(AuthAction.TYPES.SIGNOUT.FAILURE, undefined, undefined, error);

  static userResetPassword = (
    email: string,
    callback?: ICommonCallbackPayload,
  ): StandardAction<Record<string, unknown>> =>
    createPayloadAction(AuthAction.TYPES.RESET_PASSWORD.START, { email, callback });

  static userResetPasswordSuccess = (): StandardAction<string> =>
    createPayloadAction(AuthAction.TYPES.RESET_PASSWORD.SUCCESS);

  static userResetPasswordError = (error?: string): StandardAction =>
    createPayloadAction(AuthAction.TYPES.RESET_PASSWORD.FAILURE, undefined, undefined, error);

  static verifyToken = (
    token: string,
    type: TokenType,
    callback?: CallBack,
  ): StandardAction<{ token: string; type: TokenType; callback?: CallBack }> =>
    createPayloadAction(AuthAction.TYPES.VERIFY_TOKEN.START, { token, type, callback });

  static verifyTokenSuccess = (): StandardAction<Record<string, unknown>> =>
    createPayloadAction(AuthAction.TYPES.VERIFY_TOKEN.SUCCESS);

  static verifyTokenFailure = (error: string): StandardAction<undefined> =>
    createPayloadAction(AuthAction.TYPES.VERIFY_TOKEN.FAILURE, undefined, undefined, error);

  static setInitUrl = (initURL: string): StandardAction<{ initURL: string }> =>
    createPayloadAction(AuthAction.TYPES.INIT_URL, { initURL });

  static getNewAccessToken = (callback?: ICommonCallbackPayload): StandardAction<Record<string, unknown>> =>
    createPayloadAction(AuthAction.TYPES.GET_NEW_ACCESS_TOKEN.START, { callback });

  static getNewAccessTokenEnd = (callback?: ICommonCallbackPayload): StandardAction<Record<string, unknown>> =>
    createPayloadAction(AuthAction.TYPES.GET_NEW_ACCESS_TOKEN.END, { callback });
}
